import React from 'react';
export interface CalendarVisualData {
  year: number;
  month: number;
  highlightDates?: number[];
  todayDate?: number;
  targetDate?: number;
  secondMonth?: number;
  secondMonthYear?: number;
  secondMonthHighlight?: number[];
  secondMonthTarget?: number;
  label?: string;
}
const MONTH_NAMES = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
const DAY_HEADERS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const getDaysInMonth = (year: number, month: number) => {
  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysPerMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return daysPerMonth[month - 1];
};
const getStartDay = (year: number, month: number) => {
  const day = new Date(year, month - 1, 1).getDay();
  return day === 0 ? 6 : day - 1; // Convert Sunday=0 to Monday=0
};
const getWeekNumber = (d: Date) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};
const MonthGrid: React.FC<{
  year: number;
  month: number;
  highlightDates?: number[];
  todayDate?: number;
  targetDate?: number;
}> = ({
  year,
  month,
  highlightDates = [],
  todayDate,
  targetDate
}) => {
  const daysInMonth = getDaysInMonth(year, month);
  const startDay = getStartDay(year, month);
  const monthName = MONTH_NAMES[month - 1];
  const days = Array.from({
    length: daysInMonth
  }, (_, i) => i + 1);
  const emptyCells = Array.from({
    length: startDay
  }, (_, i) => i);
  // Calculate week numbers for each row
  const totalCells = startDay + daysInMonth;
  const numRows = Math.ceil(totalCells / 7);
  const weekNumbers = Array.from({
    length: numRows
  }, (_, i) => {
    const dayInWeek = i === 0 ? 1 : i * 7 - startDay + 1;
    return getWeekNumber(new Date(year, month - 1, dayInWeek));
  });
  return <div className="bg-white rounded-xl shadow-sm border border-orange-100 overflow-hidden w-full max-w-[400px] mx-auto" data-id="element-11415">
      <div className="bg-orange-500 text-white text-center py-3 font-bold text-lg capitalize" data-id="element-11416">
        {monthName} {year}
      </div>
      <div className="p-4 flex gap-2" data-id="element-11417">
        {/* Week numbers column */}
        <div className="flex flex-col pt-6 border-r border-orange-100 pr-2" data-id="element-11418">
          {weekNumbers.map((wn, i) => <div key={`wn-${i}`} className="h-8 w-6 flex items-center justify-center text-[10px] font-bold text-orange-300 mb-1" data-id="element-11419">
              W{wn}
            </div>)}
        </div>

        {/* Days grid */}
        <div className="flex-1" data-id="element-11420">
          <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-11421">
            {DAY_HEADERS.map((day, index) => <div key={day} className={`text-center text-xs font-bold uppercase ${index >= 5 ? 'text-orange-300' : 'text-orange-600'}`} data-id="element-11422">
                {day}
              </div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-id="element-11423">
            {emptyCells.map(cell => <div key={`empty-${cell}`} className="h-8 w-8 mb-1" data-id="element-11424" />)}
            {days.map(day => {
            const isToday = day === todayDate;
            const isTarget = day === targetDate;
            const isHighlighted = highlightDates.includes(day);
            const dayOfWeek = (startDay + day - 1) % 7;
            const isWeekend = dayOfWeek >= 5;
            let baseClasses = 'h-8 w-8 flex items-center justify-center rounded-full text-sm font-medium transition-colors mb-1';
            let stateClasses = '';
            if (isTarget) {
              stateClasses = 'bg-amber-500 text-white shadow-md ring-2 ring-amber-200 ring-offset-1 font-bold';
            } else if (isToday) {
              stateClasses = 'border-2 border-blue-500 text-blue-700 font-bold bg-blue-50';
            } else if (isHighlighted) {
              stateClasses = 'bg-orange-100 text-orange-800';
            } else {
              stateClasses = isWeekend ? 'text-slate-400' : 'text-slate-700';
            }
            return <div key={day} className="flex items-center justify-center" data-id="element-11425">
                  <div className={`${baseClasses} ${stateClasses}`} data-id="element-11426">{day}</div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </div>;
};
export function CalendarVisual({
  year,
  month,
  highlightDates,
  todayDate,
  targetDate,
  secondMonth,
  secondMonthYear,
  secondMonthHighlight,
  secondMonthTarget,
  label
}: CalendarVisualData) {
  return <div className="flex flex-col items-center gap-6 w-full py-4" data-id="element-11427">
      {label && <h3 className="text-lg font-semibold text-slate-800 text-center" data-id="element-11428">
          {label}
        </h3>}
      <div className="flex flex-col md:flex-row gap-6 justify-center items-start w-full" data-id="element-11429">
        <MonthGrid year={year} month={month} highlightDates={highlightDates} todayDate={todayDate} targetDate={targetDate} data-id="element-11430" />
        {secondMonth && <MonthGrid year={secondMonthYear || year} month={secondMonth} highlightDates={secondMonthHighlight} targetDate={secondMonthTarget} data-id="element-11431" />}
      </div>
    </div>;
}